import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import styles from './EditProfile.module.css';
import ProfileImageUploader from './ProfileImageUploader';
import { MapContainer, TileLayer, Marker, useMapEvents } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { MAP_TILE_URL, MAP_TILE_ATTRIBUTION } from '../../config/mapTile';
import pinIconUrl from '../../assets/pin.svg';

const venuePinIcon = L.icon({
  iconUrl: pinIconUrl,
  iconSize: [36, 36],
  iconAnchor: [18, 36],
});

const VenueMarker = ({ setLatLng }) => {
  useMapEvents({
    click(e) {
      setLatLng(e.latlng);
    },
  });
  return null;
};

const EditProfile = () => {
  const { user: authUser, refreshUser } = useAuth();
  const [form, setForm] = useState({
    name: '',
    email: '',
    phone: '',
    organization_name: '',
    twitter: '',
    instagram: '',
    role: 'organizer',
    venue_name: '',
    address: '',
    description: '',
    latitude: null,
    longitude: null,
  });
  const [latLng, setLatLng] = useState(null);
  const [loading, setLoading] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({});
  const [success, setSuccess] = useState('');
  const [sellerTab, setSellerTab] = useState('profile');

  const getFieldError = (name) => fieldErrors?.[name]?.[0] || '';

  const clearFieldError = (name) => {
    setFieldErrors((prev) => {
      if (!prev?.[name]) return prev;
      const next = { ...prev };
      delete next[name];
      return next;
    });
  };

  // 詳細ユーザー情報を取得して初期値に反映
  useEffect(() => {
    const fetchProfile = async () => {
      if (!authUser?.id) return;
      try {
        const res = await axios.get(`${process.env.REACT_APP_API_URL}/me/profile`);
        const user = res.data;
        setForm({
          name: user.name || '',
          email: user.email || '',
          phone: user.phone || '',
          organization_name: user.organization_name || '',
          twitter: user.twitter ? user.twitter.replace('https://x.com/', '') : '',
          instagram: user.instagram ? user.instagram.replace('https://www.instagram.com/', '') : '',
          role: user.role || 'organizer',
          venue_name: user.venue?.name || '',
          address: user.venue?.address || '',
          description: user.description || user.venue?.description || '',
          latitude: user.venue?.latitude || null,
          longitude: user.venue?.longitude || null,
        });
        if (user.venue?.latitude && user.venue?.longitude) {
          setLatLng({ lat: user.venue.latitude, lng: user.venue.longitude });
        }
      } catch (e) {
        // fallback: authUserのみ
        setForm(f => ({
          ...f,
          name: authUser.name || '',
          email: authUser.email || '',
        }));
      }
    };
    fetchProfile();
  }, [authUser?.id]);

  useEffect(() => {
    if (form.role !== 'venue' && sellerTab === 'address') {
      setSellerTab('profile');
    }
  }, [form.role, sellerTab]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm({ ...form, [name]: value });
    clearFieldError(name);
    clearFieldError('submit');
  };

  const validateSNSId = (id) => {
    if (!id) return true;
    return /^[A-Za-z0-9_\.]{1,30}$/.test(id);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setFieldErrors({});
    setSuccess('');
    if (!validateSNSId(form.twitter)) {
      setFieldErrors({ twitter: ['X（旧Twitter）のユーザーIDが正しくありません。'] });
      setLoading(false);
      return;
    }
    if (!validateSNSId(form.instagram)) {
      setFieldErrors({ instagram: ['InstagramのユーザーIDが正しくありません。'] });
      setLoading(false);
      return;
    }
    if (form.role === 'artist' && !form.organization_name.trim()) {
      setFieldErrors({ organization_name: ['アーティスト名を入力してください。'] });
      setLoading(false);
      return;
    }
    // 購入者向け公開設定は廃止
    if (form.role === 'venue' && !latLng) {
      setFieldErrors({ latitude: ['地図上で会場の位置を選択してください。'] });
      setLoading(false);
      return;
    }
    const data = {
      ...form,
      latitude: latLng?.lat,
      longitude: latLng?.lng,
      twitter: form.twitter || '',
      instagram: form.instagram || '',
    };
    try {
      const res = await axios.put(`${process.env.REACT_APP_API_URL}/user/profile`, data);
      if (res.data.email_changed) {
        setSuccess('メールアドレスを変更しました。再度メール認証が必要です。ログインし直してください。');
        setTimeout(() => {
          window.location.href = '/login';
        }, 2000);
        return;
      }

      try {
        if (refreshUser) await refreshUser();
      } catch (err) {
        // ignore
      }

      setSuccess('プロフィールを更新しました');
    } catch (err) {
      const data = err?.response?.data;
      if (data?.errors && typeof data.errors === 'object') {
        const normalizedFieldErrors = {};
        Object.entries(data.errors).forEach(([key, value]) => {
          const messages = Array.isArray(value) ? value.map((msg) => String(msg)) : [String(value)];
          normalizedFieldErrors[key] = messages;
        });
        setFieldErrors(normalizedFieldErrors);
      } else if (data?.message) {
        setFieldErrors({ submit: [String(data.message)] });
      } else {
        setFieldErrors({ submit: ['更新に失敗しました。'] });
      }
    } finally {
      setLoading(false);
    }
  };

  if (authUser && authUser.role === 'buyer') {
    // 購入者は編集画面非表示
    return null;
  }

  if (authUser && authUser.email_verified_at === null) {
    return (
      <div className={styles.container}>
        <h2 className={styles.heading}>プロフィール編集</h2>
        <div style={{ color: 'red', margin: '24px 0', fontWeight: 'bold' }}>
          メール認証が完了していません。認証メールをご確認ください。
        </div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <h2 className={styles.heading}>プロフィール編集</h2>
      {authUser && authUser.role !== 'buyer' && (
        <p style={{ margin: '0 0 12px', color: '#4b5563', fontSize: 14 }}>変更したい項目をタブで選択してください。</p>
      )}
      {authUser && authUser.role !== 'buyer' && (
        <div className={styles.tabs}>
          <button
            type="button"
            className={`${styles.tab} ${sellerTab === 'profile' ? styles.tabActive : ''}`}
            onClick={() => setSellerTab('profile')}
          >
            プロフィール
          </button>
          <button
            type="button"
            className={`${styles.tab} ${sellerTab === 'sns' ? styles.tabActive : ''}`}
            onClick={() => setSellerTab('sns')}
          >
            SNS
          </button>
          {form.role === 'venue' && (
            <button
              type="button"
              className={`${styles.tab} ${sellerTab === 'address' ? styles.tabActive : ''}`}
              onClick={() => setSellerTab('address')}
            >
              住所
            </button>
          )}
        </div>
      )}
      <form onSubmit={handleSubmit} className={styles.form}>
          <>
            {sellerTab === 'profile' && (
              <>
                <label className={styles.inputLabel}>プロフィール画像</label>
                <ProfileImageUploader />

                {form.role === 'organizer' && (
                  <>
                    <label className={styles.inputLabel}>イベント団体名（活動名）</label>
                    <input
                      type="text"
                      name="organization_name"
                      value={form.organization_name}
                      onChange={handleChange}
                      className={getFieldError('organization_name') ? styles.inputError : ''}
                      required
                    />
                    {getFieldError('organization_name') && <p className={styles.fieldError}>{getFieldError('organization_name')}</p>}
                    <div style={{ height: 8 }} />
                  </>
                )}

                {form.role === 'artist' && (
                  <>
                    <label className={styles.inputLabel}>アーティスト名</label>
                    <input
                      type="text"
                      name="organization_name"
                      value={form.organization_name}
                      onChange={handleChange}
                      className={getFieldError('organization_name') ? styles.inputError : ''}
                      required
                    />
                    {getFieldError('organization_name') && <p className={styles.fieldError}>{getFieldError('organization_name')}</p>}
                    <div style={{ height: 8 }} />
                  </>
                )}

                {form.role === 'venue' && (
                  <>
                    <label className={styles.inputLabel}>会場名</label>
                    <input
                      type="text"
                      name="venue_name"
                      value={form.venue_name}
                      onChange={handleChange}
                      className={getFieldError('venue_name') ? styles.inputError : ''}
                      required
                    />
                    {getFieldError('venue_name') && <p className={styles.fieldError}>{getFieldError('venue_name')}</p>}
                    <div style={{ height: 8 }} />
                  </>
                )}

                <label className={styles.inputLabel}>説明（任意）</label>
                <textarea
                  name="description"
                  value={form.description}
                  onChange={handleChange}
                  placeholder="プロフィール説明（任意）"
                  className={getFieldError('description') ? styles.inputError : ''}
                />
                {getFieldError('description') && <p className={styles.fieldError}>{getFieldError('description')}</p>}
              </>
            )}

            {sellerTab === 'sns' && (
              <>
                <label className={styles.inputLabel}>X（旧Twitter）ユーザーID（任意）</label>
                <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                  <span style={{ color: '#888', fontSize: 13 }}>https://x.com/</span>
                  <input
                    type="text"
                    name="twitter"
                    value={form.twitter}
                    onChange={handleChange}
                    pattern="[A-Za-z0-9_\.]{1,30}"
                    inputMode="text"
                    className={getFieldError('twitter') ? styles.inputError : ''}
                    style={{ flex: 1 }}
                  />
                </div>
                {getFieldError('twitter') && <p className={styles.fieldError}>{getFieldError('twitter')}</p>}
                <div style={{ height: 8 }} />
                <label className={styles.inputLabel}>InstagramユーザーID（任意）</label>
                <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                  <span style={{ color: '#888', fontSize: 13 }}>https://www.instagram.com/</span>
                  <input
                    type="text"
                    name="instagram"
                    value={form.instagram}
                    onChange={handleChange}
                    pattern="[A-Za-z0-9_\.]{1,30}"
                    inputMode="text"
                    className={getFieldError('instagram') ? styles.inputError : ''}
                    style={{ flex: 1 }}
                  />
                </div>
                {getFieldError('instagram') && <p className={styles.fieldError}>{getFieldError('instagram')}</p>}
              </>
            )}

            {form.role === 'venue' && sellerTab === 'address' && (
              <>
                <label className={styles.inputLabel}>住所</label>
                <input
                  type="text"
                  name="address"
                  value={form.address}
                  onChange={handleChange}
                  className={getFieldError('address') ? styles.inputError : ''}
                  required
                />
                {getFieldError('address') && <p className={styles.fieldError}>{getFieldError('address')}</p>}
                <div style={{ height: 8 }} />
                <div style={{ marginBottom: 4, color: '#555', fontSize: 14 }}>地図をクリックして場所を選択</div>
                <div className={getFieldError('latitude') ? styles.mapError : ''}>
                  <MapContainer center={latLng || [35.68, 139.76]} zoom={12} style={{ height: '300px', marginBottom: '10px' }}>
                    <TileLayer url={MAP_TILE_URL} attribution={MAP_TILE_ATTRIBUTION} />
                    <VenueMarker
                      setLatLng={(value) => {
                        setLatLng(value);
                        clearFieldError('latitude');
                      }}
                    />
                    {latLng && <Marker position={latLng} icon={venuePinIcon} />}
                  </MapContainer>
                </div>
                {getFieldError('latitude') && <p className={styles.fieldError}>{getFieldError('latitude')}</p>}
              </>
            )}

            <div style={{ height: 8 }} />
            <button type="submit" disabled={loading}>保存</button>
            {getFieldError('submit') && <div className={styles.fieldError}>{getFieldError('submit')}</div>}
            {success && <div style={{ color: 'green', marginTop: 8 }}>{success}</div>}
          </>
      </form>
    </div>
  );
};

export default EditProfile;
